import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import { useIntl } from '@mfw/components';
import Box from '@mui/material/Box';
import React from 'react';

import {
  TermsOfUseDisclaimer,
  TermsOfUseLoadState,
  getTermsOfUseQuestionTitle,
} from '../TermsOfUse';
import {
  useUserMembershipTermsOfUse,
  useAcceptMembershipTermsOfUse,
} from '../../hooks/memberships';
import { useMembershipCardSetupStore } from '../../store/useMembershipCardSetupStore';
import { useMembershipTouAcceptanceStore } from '../../store/useMembershipTouAcceptanceStore';
import { useSnackbar } from '../../context';
import messages from './membershipTouAcceptanceDialog.messages';

/**
 * Global, always-mounted (per `useDailyActivityModalStore`'s precedent) —
 * driven entirely by `useMembershipTouAcceptanceStore`, opened either from a
 * `MEMBERSHIP_TOU_UPDATED` notification click or right after
 * `AssignMembershipDialog` assigns a client's first RECURRING membership.
 * When card activation is waiting for these terms, acceptance resumes that
 * same intent. Standalone acceptance (staff/notification) never starts billing.
 */
export const MembershipTouAcceptanceDialog = () => {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { pending, clear } = useMembershipTouAcceptanceStore();
  const { resumeAfterTerms, cancelTerms } = useMembershipCardSetupStore();
  const [acceptedVersionId, setAcceptedVersionId] = React.useState<
    string | null
  >(null);
  const { mutate: accept, isPending: isAccepting } =
    useAcceptMembershipTermsOfUse();

  const {
    data: termsOfUse,
    isPending: termsPending,
    isError: termsError,
  } = useUserMembershipTermsOfUse({
    clientId: pending?.clientId,
    enabled: Boolean(pending),
    organizationId: pending?.organizationId,
    userMembershipId: pending?.userMembershipId,
  });

  const answerByQuestionId = React.useMemo(
    () => new Map(termsOfUse?.answers.map((a) => [a.questionId, a.textValue])),
    [termsOfUse],
  );
  const accepted =
    Boolean(termsOfUse?.version) &&
    acceptedVersionId === termsOfUse?.version?.id;

  const close = () => {
    if (pending) cancelTerms(pending);
    clear();
    setAcceptedVersionId(null);
  };

  const confirm = () => {
    if (!pending || !termsOfUse?.version || !accepted) return;
    accept(
      { ...pending, versionId: termsOfUse.version.id },
      {
        onSuccess: () => {
          enqueueSnackbar({
            message: formatMessage(messages.accepted),
            variant: 'success',
          });
          resumeAfterTerms(pending);
          close();
        },
      },
    );
  };

  return (
    <Dialog onClose={close} open={Boolean(pending)} maxWidth="sm" fullWidth>
      <DialogTitle>{formatMessage(messages.title)}</DialogTitle>
      <DialogContent dividers>
        {!termsOfUse?.version && (
          <TermsOfUseLoadState
            isPending={termsPending}
            isError={termsError}
            isUnavailable={Boolean(termsOfUse) && !termsOfUse?.version}
          />
        )}
        {termsOfUse?.version &&
          termsOfUse.questions.map((question) => (
            <Box key={question.id} mb={2}>
              <Typography variant="subtitle2">
                {getTermsOfUseQuestionTitle(
                  {
                    questionKey: question.key,
                    questionText: question.questionText,
                  },
                  formatMessage,
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {answerByQuestionId.get(question.id)}
              </Typography>
            </Box>
          ))}
        {termsOfUse?.version && <TermsOfUseDisclaimer />}
        {termsOfUse?.version && (
          <FormControlLabel
            control={
              <Checkbox
                data-testid="membership-tou-accept-checkbox"
                checked={accepted}
                onChange={(_event, checked) =>
                  setAcceptedVersionId(
                    checked ? (termsOfUse.version?.id ?? null) : null,
                  )
                }
              />
            }
            label={formatMessage(messages.acceptLabel)}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button data-testid="membership-tou-cancel" onClick={close}>
          {formatMessage(messages.cancel)}
        </Button>
        <Button
          data-testid="membership-tou-confirm"
          onClick={confirm}
          disabled={!termsOfUse?.version || !accepted || isAccepting}
          variant="contained"
        >
          {formatMessage(messages.confirm)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MembershipTouAcceptanceDialog;
